import React, { useState } from "react";
import { Helmet } from "react-helmet";
import {
  Card,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Box,
  Container,
  Typography,
  makeStyles,
} from "@material-ui/core";
import PerfectScrollbar from "react-perfect-scrollbar";
import { connect } from "react-redux";
import { fetch_transactions_async } from "src/actions/transaction.actions";

const TOKENS = {
  surface: "#FFFFFF",
  border: "#EEF0F5",
  textPrimary: "#0F172A",
  textSecondary: "#64748B",
  textMuted: "#94A3B8",
  accent: "#222222",
  divider: "#F1F5F9",
};

const useStyles = makeStyles(() => ({
  root: {
    backgroundColor: "#F5F6FA",
    minHeight: "100%",
    paddingTop: 24,
    paddingBottom: 48,
  },
  pageHeader: { marginBottom: 24 },
  pageTitle: { fontSize: 24, fontWeight: 700, color: TOKENS.textPrimary },
  pageSubtitle: { fontSize: 14, color: TOKENS.textSecondary, marginTop: 2 },
  card: {
    backgroundColor: TOKENS.surface,
    border: `1px solid ${TOKENS.border}`,
    borderRadius: 16,
    boxShadow: "none",
    overflow: "hidden",
  },
  tableHead: { backgroundColor: "#FAFBFC" },
  tableHeadCell: {
    color: TOKENS.textSecondary,
    fontSize: 12,
    fontWeight: 600,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    borderBottom: `1px solid ${TOKENS.border}`,
    padding: "14px 20px",
  },
  tableRow: {
    transition: "background 0.15s ease",
    "&:hover": { backgroundColor: "#FAFBFC" },
  },
  tableCell: {
    padding: "16px 20px",
    borderBottom: `1px solid ${TOKENS.divider}`,
    fontSize: 14,
    color: TOKENS.textPrimary,
    verticalAlign: "middle",
  },
  nameCell: {
    display: "flex",
    flexDirection: "column",
  },
  nameText: {
    fontSize: 14,
    fontWeight: 600,
    color: TOKENS.textPrimary,
    lineHeight: 1.3,
  },
  emailText: {
    fontSize: 12,
    color: TOKENS.textMuted,
    marginTop: 1,
  },
  amountText: {
    fontSize: 14,
    fontWeight: 600,
    color: TOKENS.textPrimary,
  },
  amountCurrency: {
    fontSize: 12,
    color: TOKENS.textMuted,
    fontWeight: 400,
    marginRight: 4,
  },
  datePill: {
    display: "inline-flex",
    alignItems: "center",
    padding: "4px 10px",
    borderRadius: 8,
    fontSize: 12,
    fontWeight: 500,
    backgroundColor: TOKENS.divider,
    color: TOKENS.textPrimary,
    whiteSpace: "nowrap",
  },
  listingPill: {
    display: "inline-flex",
    alignItems: "center",
    padding: "4px 10px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 500,
    backgroundColor: "#EEF2FF",
    color: "#000000",
    maxWidth: 200,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  emptyState: { padding: "64px 20px", textAlign: "center" },
  emptyIcon: {
    width: 56,
    height: 56,
    borderRadius: 16,
    backgroundColor: "#F2F2F2",
    color: TOKENS.accent,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto 16px",
    fontSize: 24,
  },
  emptyTitle: {
    fontSize: 16,
    fontWeight: 700,
    color: TOKENS.textPrimary,
    marginBottom: 4,
  },
  emptySub: { fontSize: 13, color: TOKENS.textSecondary },
  pagination: {
    borderTop: `1px solid ${TOKENS.border}`,
    "& .MuiTablePagination-toolbar": { paddingLeft: 16, paddingRight: 8 },
    "& .MuiTablePagination-caption": {
      fontSize: 13,
      color: TOKENS.textSecondary,
    },
  },
}));

const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  try {
    return new Date(dateStr).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "—";
  }
};

function ViewTransactionList({ fetch_transactions_async, transactions = [] }) {
  const classes = useStyles();
  const [limit, setLimit] = useState(10);
  const [page, setPage] = useState(0);

  React.useEffect(() => {
    fetch_transactions_async();
  }, [fetch_transactions_async]);

  const handleLimitChange = (event) => {
    setLimit(parseInt(event.target.value, 10));
    setPage(0);
  };
  const handlePageChange = (_event, newPage) => setPage(newPage);

  const paged = transactions.slice(page * limit, page * limit + limit);

  return (
    <>
      <Helmet>
        <title>Tubayo Host | Transactions</title>
      </Helmet>
      <Box className={classes.root}>
        <Container maxWidth={false}>
          <Box className={classes.pageHeader}>
            <Typography className={classes.pageTitle}>Transactions</Typography>
            <Typography className={classes.pageSubtitle}>
              Payment history from your listings
            </Typography>
          </Box>

          <Card className={classes.card}>
            {transactions.length === 0 ? (
              <Box className={classes.emptyState}>
                <Box className={classes.emptyIcon}>—</Box>
                <Typography className={classes.emptyTitle}>
                  No transactions yet
                </Typography>
                <Typography className={classes.emptySub}>
                  When guests make payments, they'll appear here.
                </Typography>
              </Box>
            ) : (
              <>
                <PerfectScrollbar>
                  <Box style={{ minWidth: 800 }}>
                    <Table>
                      <TableHead className={classes.tableHead}>
                        <TableRow>
                          <TableCell className={classes.tableHeadCell}>
                            Customer
                          </TableCell>
                          <TableCell className={classes.tableHeadCell}>
                            Listing
                          </TableCell>
                          <TableCell className={classes.tableHeadCell}>
                            Amount
                          </TableCell>
                          <TableCell className={classes.tableHeadCell}>
                            Date
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {paged.map((transaction, index) => (
                          <TableRow
                            hover
                            key={transaction.id || index}
                            className={classes.tableRow}
                          >
                            <TableCell className={classes.tableCell}>
                              <Box className={classes.nameCell}>
                                <Typography className={classes.nameText}>
                                  {transaction.user_name || "—"}
                                </Typography>
                                <Typography className={classes.emailText}>
                                  {transaction.user_email || "—"}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell className={classes.tableCell}>
                              <span className={classes.listingPill}>
                                {transaction.listing_name || "—"}
                              </span>
                            </TableCell>
                            <TableCell className={classes.tableCell}>
                              <span className={classes.amountCurrency}>
                                {transaction.currency || "USD"}
                              </span>
                              <span className={classes.amountText}>
                                {transaction.amount || "0"}
                              </span>
                            </TableCell>
                            <TableCell className={classes.tableCell}>
                              <span className={classes.datePill}>
                                {formatDate(transaction.created_at)}
                              </span>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>
                </PerfectScrollbar>
                <TablePagination
                  component="div"
                  className={classes.pagination}
                  count={transactions.length}
                  onPageChange={handlePageChange}
                  onRowsPerPageChange={handleLimitChange}
                  page={page}
                  rowsPerPage={limit}
                  rowsPerPageOptions={[5, 10, 25, 50]}
                />
              </>
            )}
          </Card>
        </Container>
      </Box>
    </>
  );
}

const mapStateToProps = (state) => ({
  transactions: state.transactionReducers.transactions_data,
});

export default connect(mapStateToProps, { fetch_transactions_async })(
  ViewTransactionList,
);
